import React, {useMemo, useState} from 'react';
import AddressForm from './AddressForm';
import styles from './PropertyList.module.scss';
import {get} from 'lodash';
import Typography from 'components/Typography';
import ArrowIcon from './ArrowIcon';
import Button from 'components/Button';
import Modal from 'components/Modal';
import {Controller, useForm} from 'react-hook-form';
import FormGroup from 'components/FormGroup';
import useCompanyDetails, {CompanyPropertyForm} from '../useCompanyDetails';
import toast from 'react-hot-toast';
import {
  TypeGooglePlaceDetail,
  GooglePlaceAutoComplete,
} from 'components/GooglePlaceAutoComplete';
import TextField from 'components/TextField/TextField';
import ListWrapper from 'components/ListWrapper';
import {useLocation} from 'react-router-dom';
import MapPreview from 'components/MapPreview';
import {CompanyProperty, CreateProperty} from 'types/property';
import SelectField from 'components/SelectField';
import useCompanyConfig from 'hooks/useCompanyConfig';

type FormData = CreateProperty;

type PropertyListProps = {
  properties?: CompanyProperty[];
  isShow: boolean;
  refetch: () => void;
};

export const labels = [
  {
    label: 'Home',
    value: 'home',
  },
  {
    label: 'Work',
    value: 'work',
  },
  {
    label: 'Other',
    value: 'other',
  },
];

const ProprtyList: React.FC<PropertyListProps> = ({
  properties,
  isShow,
  refetch,
}) => {
  const [expandedId, setExpandedId] = useState<number[]>([]);
  const [openModal, setOpenModal] = useState(
    get(useLocation().state, 'isAddAddress', false)
  );
  const {labels} = useCompanyConfig();
  const title = labels.properties;
  const buttonTitle = `Add New ${labels.property}`;
  const handleOrdersClose = () => {
    setOpenModal(false);
  };

  return (
    <>
      {isShow ? (
        <div>
          <ListWrapper className={styles.listWrapper}>
            <div className={styles.accordion}>
              <div className={styles.accordion__inner}>
                <Typography variant="h3" className={styles.accordion__title}>
                  {title}
                </Typography>
                {properties?.map((property) => (
                  <div className={styles.accordion__item} key={property.id}>
                    <div
                      className={styles.accordion__item_header}
                      onClick={() =>
                        expandedId.includes(property.id)
                          ? setExpandedId((pr) =>
                              pr.filter((item) => item !== property.id)
                            )
                          : setExpandedId((pr) => [...pr, property.id])
                      }
                    >
                      <ArrowIcon
                        className={
                          expandedId.includes(property.id)
                            ? styles.accordion__item_header_icon_rotated
                            : ''
                        }
                      />{' '}
                      {property.name}
                    </div>
                    {expandedId.includes(property.id) && (
                      <div className={styles.accordion__item_content}>
                        <AddressForm defaultValues={property} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className={styles.actionWrapper}>
                <Button
                  style={{
                    backgroundColor: '#fff',
                  }}
                  onClick={() => setOpenModal(true)}
                >
                  {buttonTitle}
                </Button>
              </div>
            </div>
          </ListWrapper>
        </div>
      ) : (
        <></>
      )}
      <CreateNewProperty
        handleClose={handleOrdersClose}
        onSuccess={() => refetch()}
        isOpen={openModal}
      />
    </>
  );
};

type Props = {
  isOpen: boolean;
  handleClose: () => void;
  onSuccess: () => void;
};

export const CreateNewProperty = ({isOpen, handleClose, onSuccess}: Props) => {
  const [loading, setLoading] = useState(false);
  const {control, handleSubmit, reset, setValue} = useForm<FormData>();
  const {labels} = useCompanyConfig();

  const textContents = useMemo(() => {
    return {
      title: labels.property,
      buttonTitle: `Save ${labels.property}`,
      selectLabel: `${labels.property} ${labels.landlord}`,
      propertyName: `${labels.property} Name`,
      addressLabel: `${labels.property} Address`,
    };
  }, [labels]);
  const [location, setLocation] = useState({
    latitude: 40.7596958,
    longitude: -73.9650045,
  });

  const onSelectPlace = (placeDetail: TypeGooglePlaceDetail) => {
    setValue('address.zipcode', placeDetail.zipcode);
    setValue('address.address_line_1', placeDetail.addressLine1);
    setValue('address.city', placeDetail.city);
    setValue('address.state', placeDetail.state);
    setValue('address.latitude', placeDetail.lat);
    setValue('address.longitude', placeDetail.lon);
    setLocation({latitude: placeDetail.lat, longitude: placeDetail.lon});
  };

  const {createProperty, landlordList} = useCompanyDetails();

  const handleCreation = async (data: CreateProperty) => {
    setLoading(true);
    const {landlord, ...rest} = data;

    const body = {
      ...rest,
    } as CompanyPropertyForm;
    if (landlord) {
      body.landlord = +get(landlord, 'value', '');
    }
    try {
      await createProperty(body);
      onSuccess();
      handleClose();
      toast.success('Address created successfully');

      reset();
      setLoading(false);
    } catch (error) {
      toast.error('Something wrong with creation. Try again later !');
      setLoading(false);
    }
  };
  const landlordOptions = useMemo(
    () =>
      landlordList?.map((item) => ({
        label: `${item?.first_name} ${item?.last_name}`,
        value: String(item?.id),
      })),
    [landlordList]
  );
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      style={{
        overlay: {
          zIndex: 1000,
        },
        content: {
          overflowY: 'scroll',
        },
      }}
      className={styles.modal}
    >
      <Typography variant="h2">Add New {textContents.title}</Typography>
      <div className={styles.modalWrapper}>
        <FormGroup>
          <Controller
            name="landlord"
            control={control}
            render={({field}) => (
              <SelectField
                label={textContents.selectLabel}
                value={landlordOptions?.find(
                  (item) => item?.value === String(field.value)
                )}
                isClearable
                options={landlordOptions}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="name"
            control={control}
            render={({field, fieldState}) => (
              <TextField
                label={textContents.propertyName}
                wrapperClass={styles.googleInput}
                placeholder={`Enter ${textContents.propertyName}`}
                hasError={false}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="address.city"
            control={control}
            rules={{required: 'Please, Select the address'}}
            render={({fieldState}) => (
              <GooglePlaceAutoComplete
                label={textContents.addressLabel}
                placeholder="Address"
                wrapperClass={styles.googleInput}
                onPlaceSelected={onSelectPlace}
                error={fieldState.error?.message}
                options={{
                  types: ['address'],
                }}
              />
            )}
          />
          <Controller
            name="address.unit_address"
            control={control}
            render={({field, fieldState}) => (
              <TextField
                label="Unit Number"
                wrapperClass={styles.googleInput}
                placeholder="Enter unit number"
                hasError={false}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
        </FormGroup>

        <div
          style={{
            paddingTop: 20,
          }}
        >
          {!!location.latitude && (
            <MapPreview
              zoom={15}
              onDrag={(location) => {
                setValue('address.latitude', location.lat);
                setValue('address.longitude', location.lon);
              }}
              center={[location.latitude, location.longitude]}
            />
          )}
        </div>

        <div className={styles.actionWrapper}>
          <Button
            loading={loading}
            type="submit"
            onClick={handleSubmit(handleCreation)}
          >
            {textContents.buttonTitle}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ProprtyList;
